import type { IClientStorage, LoginState } from "@medplum/core";
import { MedplumClient, resolveId, stringify, tryGetJwtExpiration } from "@medplum/core";
import * as Sentry from "@sentry/nextjs";
import { z } from "zod";
import { clearMedplumCookieAction } from "@/actions/auth/clear-medplum-cookie";
import { notifySessionExpired } from "@/lib/auth/session-expired";
import { createLogger } from "@/lib/logger";
import { extractFetchUrlPath } from "@/lib/medplum/extract-fetch-url-path";
import {
  callWithSentrySpan,
  reportRateLimitStatusFromResponse,
} from "@/lib/medplum/report-fhir-interactions-quota-gauge";
import { logMedplumResponse } from "@/lib/medplum/response-telemetry";
import { hasStorageApi } from "@/lib/medplum/storage-utils";
import { instrumentSubscriptionDebug } from "@/lib/medplum/subscription-debug";

const logger = createLogger("medplum/clientside");

const CLIENTSIDE_STORAGE_PREFIX = "clientside-";
export const CLIENTSIDE_ACTIVE_LOGIN_STORAGE_KEY = `${CLIENTSIDE_STORAGE_PREFIX}activeLogin`;
const SharedLoginSchema = z.object({
  project: z.object({ reference: z.string() }).passthrough(),
  profile: z.object({ reference: z.string() }).passthrough(),
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

interface UnauthorizedRequest {
  activeLogin: LoginState | undefined;
  path: string;
}

/**
 * Extracts a `providerId` from a login's profile reference. The profile is a Practitioner for
 * providers and a Patient on the portal, so only the former maps to a `providerId`. Explicit null
 * so observability can filter on "unset" (see AGENTS.md).
 */
function resolveProviderIdFromLogin(activeLogin: LoginState | undefined): string | null {
  const profileReference = activeLogin?.profile?.reference;
  return profileReference?.startsWith("Practitioner/")
    ? (resolveId(activeLogin?.profile) ?? null)
    : null;
}

/**
 * Logs what is known about the access token's state the moment a 401 is first observed - before
 * `onUnauthenticated` clears the session and the token/login data is gone. A token near its
 * natural expiry (small or negative `msUntilTokenExpiry`) points at an ordinary refresh race; a
 * token with plenty of life left looks like a real revocation. Both are silent otherwise, which is
 * what made a stuck-mid-scribe session hard to diagnose after the fact.
 */
function logTokenStateBeforeSessionTeardown({
  activeLogin,
  urlPath,
  lastSuccessfulRequestAt,
}: {
  activeLogin: LoginState | undefined;
  urlPath: string;
  lastSuccessfulRequestAt: number | undefined;
}): void {
  if (!activeLogin) {
    return;
  }

  const now = Date.now();
  const tokenExpiresAtMs = activeLogin.accessToken
    ? (tryGetJwtExpiration(activeLogin.accessToken) ?? null)
    : null;
  const msUntilTokenExpiry = tokenExpiresAtMs != null ? tokenExpiresAtMs - now : null;
  const msSinceLastSuccessfulRequest =
    lastSuccessfulRequestAt != null ? now - lastSuccessfulRequestAt : null;

  logger.warn("Medplum request unauthorized; capturing token state before session teardown", {
    providerId: resolveProviderIdFromLogin(activeLogin),
    unauthorizedPath: urlPath,
    tokenExpiresAt: tokenExpiresAtMs != null ? new Date(tokenExpiresAtMs).toISOString() : null,
    msUntilTokenExpiry,
    msSinceLastSuccessfulRequest,
  });
}

/**
 * Reports a session teardown the user did not ask for.
 *
 * Clearing the cookie is not cosmetic: the next RSC render finds no session and
 * `ProviderAuthBoundary` redirects to `/login`, so one 401 can drop a provider out of an active
 * workflow - mid scribe recording, in the case that prompted this. Previously that happened with
 * no log, no Sentry event, and no toast, which is why it stayed undiagnosed.
 *
 * Only reported when a login actually existed. Public routes reach `onUnauthenticated` while
 * legitimately anonymous, and treating those as failures is the noise that made the blanket
 * redirect in #4167 untenable.
 */
export function reportSessionLoss(
  activeLogin: LoginState | undefined,
  unauthorizedPath: string | undefined,
): void {
  if (!activeLogin) {
    return;
  }

  const profileReference = activeLogin.profile?.reference;
  const providerId = resolveProviderIdFromLogin(activeLogin);
  const failedPath = unauthorizedPath ?? null;

  logger.error("Medplum session cleared after unauthorized response", {
    providerId,
    profileReference,
    unauthorizedPath: failedPath,
  });

  Sentry.captureException(new Error("Medplum session cleared after unauthorized response"), {
    tags: { feature: "auth", providerId, unauthorizedPath: failedPath },
    extra: { profileReference },
    fingerprint: ["medplum", "session-cleared-unauthenticated"],
  });
}

const TOKEN_EXCHANGE_PATH = "/oauth2/token";

/** True for the SDK's own refresh call, as opposed to a login code exchange. */
function isRefreshGrant(options: RequestInit | undefined): boolean {
  return typeof options?.body === "string" && options.body.includes("grant_type=refresh_token");
}

/**
 * Reports a refresh the token endpoint refused. Medplum rotates the refresh token on every use,
 * so this fires when the Login was revoked, its lifetime lapsed, or a copy of the token was spent
 * elsewhere, which is the one visible trace of a stolen token in use. The SDK clears the session
 * and calls `onUnauthenticated` next, but that path never saw a 401, so it reports nothing.
 */
function reportRefreshRejected(activeLogin: LoginState | undefined, status: number): void {
  const providerId = resolveProviderIdFromLogin(activeLogin);
  logger.error("Medplum refused to refresh the session", { providerId, status });
  Sentry.captureException(new Error("Medplum refused to refresh the session"), {
    tags: { feature: "auth", providerId, status: String(status) },
    fingerprint: ["medplum", "refresh-rejected"],
  });
}

/**
 * Creates a Medplum client configured for browser usage.
 */
export function createMedplumClientSideClient(): MedplumClient {
  if (!process.env.NEXT_PUBLIC_MEDPLUM_FRONTEND_CLIENT_ID) {
    throw new Error("NEXT_PUBLIC_MEDPLUM_FRONTEND_CLIENT_ID is not set");
  }

  let pendingUnauthorizedRequest: UnauthorizedRequest | undefined;
  // Diagnostic only - lets `logTokenStateBeforeSessionTeardown` report how long it has been
  // since a request last succeeded, alongside the token's own expiry.
  let lastSuccessfulRequestAt: number | undefined;
  const medplum = new MedplumClient({
    baseUrl: process.env.NEXT_PUBLIC_MEDPLUM_BASE_URL || "https://api.medplum.com/",
    clientId: process.env.NEXT_PUBLIC_MEDPLUM_FRONTEND_CLIENT_ID,

    // Use Next.js fetch
    fetch: async (url: string, options?: RequestInit) => {
      const method = options?.method ?? "GET";
      const urlPath = extractFetchUrlPath(url);

      return callWithSentrySpan({
        name: `Medplum ${method} ${urlPath}`,
        callback: async (span) => {
          const startMs = Date.now();
          // No line when fetch throws: in the browser that is mostly an offline laptop or a
          // closed tab, not Medplum. Server-side network failures are logged by createRetryFetch.
          const response = await fetch(url, options);
          const attemptMs = Date.now() - startMs;
          logMedplumResponse({
            logger,
            label: "clientside",
            method,
            urlPath,
            status: response.status,
            attemptMs,
            // the browser client never retries
            elapsedMs: attemptMs,
            attempt: 1,
          });
          reportRateLimitStatusFromResponse(response, {
            label: "clientside",
            url,
            spanOverride: span,
          });
          if (urlPath === TOKEN_EXCHANGE_PATH && !response.ok && isRefreshGrant(options)) {
            // Read while the login is still around - the SDK clears it on this response.
            reportRefreshRejected(medplum.getActiveLogin() ?? undefined, response.status);
          }
          // Capture the original 401 before Medplum clears auth and invokes its argument-less
          // callback. Don't let a failed refresh overwrite the request that started teardown.
          if (response.status === 401 && !pendingUnauthorizedRequest) {
            const activeLogin = medplum.getActiveLogin() ?? undefined;
            // Read while the login is still around - `onUnauthenticated` clears it next, and
            // that's the last chance to tell a refresh race from a real revocation.
            logTokenStateBeforeSessionTeardown({ activeLogin, urlPath, lastSuccessfulRequestAt });
            pendingUnauthorizedRequest = { activeLogin, path: urlPath };
          } else if (urlPath === TOKEN_EXCHANGE_PATH && response.ok) {
            pendingUnauthorizedRequest = undefined;
          }
          if (response.ok) {
            lastSuccessfulRequestAt = Date.now();
          }
          return response;
        },
      });
    },

    // Disable caching.
    cacheTime: 0,

    // Report the silent teardown, surface the banner, then tear down the server-side cookies.
    onUnauthenticated: async () => {
      const unauthorizedRequest = pendingUnauthorizedRequest;
      pendingUnauthorizedRequest = undefined;
      reportSessionLoss(unauthorizedRequest?.activeLogin, unauthorizedRequest?.path);
      notifySessionExpired(unauthorizedRequest?.activeLogin?.profile?.reference);
      await clearMedplumCookieAction();
    },

    storage: new ClientSideStorage(),
  });

  // Dev-only: track open WebSocket subscriptions against Medplum's per-client cap. No-op in prod.
  instrumentSubscriptionDebug(medplum);

  return medplum;
}

/** Reads the latest login written to shared browser storage by any same-origin tab. */
export function getSharedMedplumLogin(): LoginState | undefined {
  try {
    const rawLogin = localStorage.getItem(CLIENTSIDE_ACTIVE_LOGIN_STORAGE_KEY);
    if (!rawLogin) {
      return undefined;
    }
    const parsedLogin = SharedLoginSchema.safeParse(JSON.parse(rawLogin));
    return parsedLogin.success ? parsedLogin.data : undefined;
  } catch {
    return undefined;
  }
}

interface ResolveClientStorageInput {
  storage?: Storage;
}

/**
 * Resolves the most appropriate storage for browser client usage.
 */
function resolveClientStorage({ storage }: ResolveClientStorageInput): Storage {
  const providedStorage = { storage };
  if (hasStorageApi(providedStorage)) {
    return providedStorage.storage;
  }

  let globalStorage: Storage | undefined;

  // Accessing localStorage can throw in restricted contexts.
  try {
    globalStorage = typeof localStorage !== "undefined" ? localStorage : undefined;
  } catch {
    globalStorage = undefined;
  }

  const globalStorageCandidate = { storage: globalStorage };
  if (hasStorageApi(globalStorageCandidate)) {
    return globalStorageCandidate.storage;
  }

  return new InMemoryStorage();
}

class ClientSideStorage implements IClientStorage {
  private readonly storage: Storage;
  private readonly prefix = CLIENTSIDE_STORAGE_PREFIX;

  constructor(storage?: Storage) {
    this.storage = resolveClientStorage({ storage });
  }

  makeKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  clear(): void {
    for (let i = this.storage.length - 1; i >= 0; i--) {
      const key = this.storage.key(i);
      if (key?.startsWith(this.prefix)) {
        this.storage.removeItem(key);
      }
    }
  }

  getString(key: string): string | undefined {
    return this.storage.getItem(this.makeKey(key)) ?? undefined;
  }

  setString(key: string, value: string | undefined): void {
    const storageKey = this.makeKey(key);
    if (value) {
      this.storage.setItem(storageKey, value);
    } else {
      this.storage.removeItem(storageKey);
    }
  }

  getObject<T>(key: string): T | undefined {
    const str = this.getString(key);
    return str ? (JSON.parse(str) as T) : undefined;
  }

  setObject<T>(key: string, value: T): void {
    this.setString(key, value ? stringify(value) : undefined);
  }
}

/**
 * Minimal in-memory storage fallback for non-browser environments.
 */
class InMemoryStorage implements Storage {
  private readonly map = new Map<string, string>();

  get length(): number {
    return this.map.size;
  }

  clear(): void {
    this.map.clear();
  }

  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }

  key(index: number): string | null {
    return Array.from(this.map.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.map.delete(key);
  }

  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
}
